import { laptopMediumMax } from '@propertypal/shared/src/constants/mediaQueries';
import useApi from '@propertypal/shared/src/hooks/useApi';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import useLocalStorage from '@propertypal/shared/src/hooks/useLocalStorage';
import useMortgageCalculator, { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { BLACK } from '@propertypal/shared/src/styles/theme';
import { Development } from '@propertypal/shared/src/types/development';
import { BooleanKey } from '@propertypal/shared/src/types/legal-fees';
import { MarketTrendsResponse } from '@propertypal/shared/src/types/marketTrends';
import { KeyInfo, Property } from '@propertypal/shared/src/types/property';
import platform from '@propertypal/shared/src/utils/platform';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import { GalleryItem } from '@propertypal/shared/src/utils/property/getGalleryImages';
import { shouldDisplaySchoolData } from '@propertypal/shared/src/utils/schools';
import { getCategoryAdName } from '@propertypal/shared/src/utils/search/category';
import { isHandheldDevice } from '@propertypal/shared/src/utils/support';
import { AccordionHeader } from '@propertypal/web-ui/src/animation/Accordion';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import Head from 'next/head';
import { NextRouter, useRouter } from 'next/router';
import React, { FunctionComponent, useState } from 'react';
import IMAGE_LOCATOR from '../../constants/imageLocator';
import usePropertyFavourite from '../../hooks/usePropertyFavourite';
import useCallAgent from '../../utils/useCallAgent';
import AdBox from '../ads/AdBox';
import generateMetaTags from '../layout/MetaTags';
import PropertyDetailsContent from './PropertyDetailsContent';

type MarketTrendsApi = ReturnType<typeof useApi<MarketTrendsResponse>>;
type SimilarPropertiesApi = ReturnType<typeof useApi<Property[]>>;
type OtherDevelopmentsApi = ReturnType<typeof useApi<Development[]>>;

interface Props {
  property: Property;
  loggedIn: boolean;
}

export interface PropertyDetailsContentProps extends Props, ReturnType<typeof useCallAgent> {
  property: Property;
  loggedIn: boolean;
  adSuffix: string;
  images: GalleryItem[];
  updateSectionUrl: (nextSection: string, mIndex?: number) => void;
  imageIndex: number;
  updatePhotoUrl: (index: number) => void;
  handleImageClick: (image: GalleryItem) => void;
  showSlideshowModal: boolean;
  getReplaceUrl: (nextSection: string, mIndex?: number) => string;
  hasSitemap: boolean;
  hasFeaturedBrochure: boolean;
  urlParts: string[];
  favourited: boolean;
  toggleFavourited: () => Promise<boolean>;
  leadId: number;
  handleInfoClick: (keyInfo: KeyInfo) => void;
  buyingStatus: string | null;
  ukResident: '' | BooleanKey | null;
  mortgageCalculator: MortgageCalcData;
  onFavourite: ReturnType<typeof usePropertyFavourite>;
  handleShare: () => Promise<void>;
  isFeaturedDevelopment: boolean;
  similarProperties: SimilarPropertiesApi[0];
  fetchSimilarProperties: SimilarPropertiesApi[3];
  showMapModal: boolean;
  isMobile: boolean;
  openSections: string[];
  renderAccordionHeader: (key: string, text: string) => React.JSX.Element | undefined;
  showLegalFees: boolean;
  hasLegalContact: boolean;
  setEnquiryAccountNumber: React.Dispatch<React.SetStateAction<string>>;
  showSchoolData: boolean;
  showPromoteText: boolean;
  marketTrendsData: MarketTrendsApi[0];
  marketTrendsError: MarketTrendsApi[1];
  marketTrendsLoading: MarketTrendsApi[2];
  fetchMarketTrendsData: MarketTrendsApi[3];
  otherDevelopments: OtherDevelopmentsApi[0];
  fetchOtherDevelopments: OtherDevelopmentsApi[3];
  modalImage: string;
  setModalImage: React.Dispatch<React.SetStateAction<string>>;
  showStampDutyModal: boolean;
  setBuyingStatus: (newValue: string) => void;
  setUkResident: (newValue: '' | BooleanKey) => void;
  handleBack: () => void;
  showEnquiryModal: boolean;
  showRegisterInterestModal: boolean;
  showRequestValuationModal: boolean;
  enquiryAccountNumber: string;
  setLeadId: React.Dispatch<React.SetStateAction<number>>;
  showLegalFeesModal: boolean;
  showBrochureModal: boolean;
  showReportModal: boolean;
  showShareModal: boolean;
  isAdObscuringModalOpen: boolean;
}

export const PROPERTY_IMAGE_LOCATOR = ['hd', '580x425:FILL_CROP', '2356x1666:AUTO'];

const AD_BOX_1_SIZES = [[320, 50], 'fluid'];
const AD_BOX_1_STYLE = { backgroundColor: BLACK, padding: '10px 0' };

const getPhotoId = (url: string) => {
  const result = /photo-\d+/.exec(url);

  if (result && result[0]) {
    return parseInt(result[0].replace('photo-', ''), 10) - 1;
  }

  return 0;
};

export const routerUpdate = (router: NextRouter, nextUrl: string) => {
  return router.replace(nextUrl, undefined, {
    shallow: true,
  });
};

const PropertyDetails: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const isMobile = useMediaQuery({ query: `(${laptopMediumMax})` });
  // ignore query-strings when getting url parts
  const urlParts = router.asPath.split('?')[0].split('/');
  const [buyingStatus, setBuyingStatus] = useLocalStorage<string>('buyingStatus', '');
  const [ukResident, setUkResident] = useLocalStorage<BooleanKey | ''>('ukResident', 'TRUE');
  const callAgent = useCallAgent();
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [favourited, setFavourited] = useState<boolean>(props.property.shortlisted || false);
  const [modalImage, setModalImage] = useState<string>('');
  const [leadId, setLeadId] = useState(0);
  const imageIndex = getPhotoId(router.asPath);
  const [enquiryAccountNumber, setEnquiryAccountNumber] = useState('');
  const showMapModal = urlParts.includes('map');
  const showShareModal = urlParts.includes('share');
  const showEnquiryModal = urlParts.includes('enquiry');
  const showRegisterInterestModal = props.property.registerInterest && urlParts.includes('register-interest');
  const showRequestValuationModal = props.property.requestValuation && urlParts.includes('request-valuation');
  const showReportModal = urlParts.includes('report-a-problem');
  const showStampDutyModal = urlParts.includes('stamp-duty');
  const showBrochureModal = urlParts.includes('brochure');
  const showNotesModal = urlParts.includes('notes');
  const showImportantPlacesModal = urlParts.includes('important-places');
  const showSlideshowModal = !!urlParts.find((part) =>
    /^(slideshow|gallery|video|sitemap|floor-plans.*|tour\d+)$/.test(part),
  );
  const isFeaturedDevelopment = !!props.property.featuredDevelopmentContent;
  // Only featured development/units can have sitemap displayed
  const hasSitemap = !!props.property.images?.find((i) => i.imageType === 'SiteMap');
  const hasFeaturedBrochure =
    (props.property.development || props.property.type === 'development') &&
    isFeaturedDevelopment &&
    !!props.property.brochures?.length;
  const showSchoolData = shouldDisplaySchoolData(props.property);
  const images = getGalleryImages(props.property, PROPERTY_IMAGE_LOCATOR[0], true, true);
  const adSuffix = getCategoryAdName(props.property.propertyType?.key, props.property.saleType.key);
  const [marketTrendsData, marketTrendsError, marketTrendsLoading, fetchMarketTrendsData] =
    useApi<MarketTrendsResponse>(`/market-trends/property/${props.property.id}`, false);
  const similarUrl = `/property/${props.property.id}/${
    props.property.development ? 'other-types' : 'similar'
  }?pageSize=${platform.title === 'Propertynews' ? 4 : 3}&imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}`;
  const [similarProperties, , , fetchSimilarProperties] = useApi<Property[]>(similarUrl, false);
  const [otherDevelopments, , , fetchOtherDevelopments] = useApi<Development[]>(
    `/development/${props.property.id}/other-developments?imageLocator=${encodeURIComponent(IMAGE_LOCATOR)}`,
    false,
  );
  const mortgageCalculator = useMortgageCalculator(props.property);
  const showPromoteText = !!(
    props.property.type === 'property' &&
    !props.property.development &&
    props.property.countryCode === 'GBR' &&
    props.property.published &&
    // also should not show when property is sold/let
    props.property.status &&
    !['saleAgreed', 'sold', 'let', 'letAgreed'].includes(props.property.status?.key)
  );
  const onFavourite = usePropertyFavourite(props.loggedIn);
  const showLegalFees = !!(
    props.property.country?.isoCode3 === 'NIR' &&
    props.property.status?.key === 'forSale' &&
    !props.property.development &&
    props.property.propertyType?.key === 'residential' &&
    props.property.published &&
    props.property.saleType?.key === 'sale' &&
    props.property.type === 'property'
  );
  const hasLegalContact =
    !!(
      showLegalFees &&
      props.property?.price &&
      props?.property?.price?.price &&
      props?.property?.price?.price > 500000
    ) || !!(props.property.price?.priceOnApplication || props.property.price?.priceHidden);
  const showLegalFeesModal = showLegalFees && !hasLegalContact && urlParts.includes('legal-fees');

  const isAdObscuringModalOpen =
    showMapModal ||
    showShareModal ||
    showEnquiryModal ||
    showReportModal ||
    showBrochureModal ||
    showSlideshowModal ||
    showStampDutyModal ||
    showRegisterInterestModal ||
    showRequestValuationModal ||
    showLegalFeesModal ||
    showNotesModal ||
    showImportantPlacesModal;

  const getReplaceUrl = (nextSection: string, mIndex?: number) => {
    const [currentUrl, qs] = router.asPath.split('?');
    const photoIndex: string | undefined =
      mIndex !== undefined ? (mIndex + 1).toString() : currentUrl.split('/photo-')[1];
    const photo = photoIndex && photoIndex !== '0' ? `/photo-${photoIndex}` : '';

    return `${props.property.path}${nextSection}${photo}${qs ? `?${qs}` : ''}`;
  };

  const updateSectionUrl = (nextSection: string, mIndex?: number) => {
    const nextUrl = getReplaceUrl(nextSection, mIndex);

    routerUpdate(router, nextUrl);
  };

  const updatePhotoUrl = (index: number) => {
    const currentIndex = router.asPath.split('?')[0].split('photo-')[1] || '1';

    if (index !== parseInt(currentIndex, 10) - 1) {
      const url = router.asPath.replace(/\/photo-\d+.*/, '');
      const [currentUrl, qs] = url.split('?');
      const photo = index > 0 ? `/photo-${index + 1}` : '';

      router.replace(`${currentUrl}${photo}${qs ? `?${qs}` : ''}`, undefined, {
        shallow: true,
      });
    }
  };

  const toggleFavourited = async () => {
    const loggedIn = await onFavourite()(!favourited, props.property.id);

    if (loggedIn) setFavourited(!favourited);

    return loggedIn;
  };

  const handleBack = () => {
    updateSectionUrl('');
  };

  const handleImageClick = (image: GalleryItem) => {
    if (image.type === 'webview' && image.insecure) {
      window.open(image.link, image.text, 'popup');
    } else if (image.type === 'webview') {
      updateSectionUrl(`/tour${image.tourIndex}`);
    } else if (image.type === 'video') {
      updateSectionUrl('/video');
    } else if (image.type === 'sitemap') {
      updateSectionUrl('/sitemap');
    } else {
      updateSectionUrl('/slideshow');
    }
  };

  const handleShare = async () => {
    if (navigator.share && isHandheldDevice()) {
      try {
        await navigator.share({
          url: `${platform.url}/${props.property.pathId}`,
          text: `Found this on ${platform.title}`,
        });
      } catch {
        // hide native share errors. Commonly happens if share button is pressed twice in quick succession
      }
    } else {
      updateSectionUrl('/share');
    }
  };

  const handleInfoClick = (keyInfo: KeyInfo) => {
    if (keyInfo.image) {
      setModalImage(keyInfo.image);
    }

    if (keyInfo.key === 'EPC_FILES' && keyInfo.link) {
      window.open(keyInfo.link);
    }

    if (keyInfo.key === 'STAMP_DUTY_COMPLEX') {
      trackGaEvent('key_info_stamp_duty_change', { page: 'PDP', location: 'Key Information' });

      router.replace(`${props.property.path}/stamp-duty`, undefined, { shallow: true });
    }

    if (keyInfo.key === 'TYPICAL_MORTGAGE') {
      trackGaEvent('key_info_mortgage_click', { page: 'PDP', location: 'Key Information' });

      if (isMobile && !openSections.includes('mortgageCalculator')) {
        setOpenSections([...openSections, 'mortgageCalculator']);
      }

      const mortgageCalc = document.getElementById('calculator-marker');
      mortgageCalc?.scrollIntoView({ behavior: 'smooth' });
    }

    if (keyInfo.key === 'LEGAL_CALCULATOR') {
      trackGaEvent('legal_fees_hyperlink_click', {
        page: 'PDP',
        location: 'Key Information',
        '500k': !!(props.property?.price?.price && props.property?.price?.price > 500000),
        price: props.property?.price?.priceOnApplication ? 'Price Not Provided' : props.property?.price?.price,
      });

      if (isMobile && !openSections.includes('legalFees')) {
        setOpenSections([...openSections, 'legalFees']);
      }

      const legalCalc = document.getElementById('legal-fees-marker');
      legalCalc?.scrollIntoView({ behavior: 'smooth' });
    }

    if (keyInfo.key === 'BROADBAND') {
      trackGaEvent('key_info_broadband_click', { page: 'PDP', location: 'Key Information' });
      setOpenSections(['broadbandTable']);
      const broadbandTable = document.getElementById('broadband-table');
      broadbandTable?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderAccordionHeader = (key: string, text: string) => {
    if (!isMobile) return undefined;

    const toggleOpen = () => {
      if (openSections.includes(key)) {
        const updatedSections = openSections.filter((section) => section !== key);
        setOpenSections(updatedSections);
      } else {
        setOpenSections([...openSections, key]);
      }
    };
    return <AccordionHeader active={openSections.includes(key)} text={text} onClick={toggleOpen} testID={key} />;
  };

  useEventListener('beforeprint', () => {
    if (props.property.type === 'property') {
      window.open(`/property-print?property=${props.property.id}`);
    }
  });

  return (
    <>
      <Head>
        {generateMetaTags({
          title: props.property.metaDescriptions.title,
          url: props.property.path,
          description: props.property.metaDescriptions.description,
          accountNumber: props.property.account.accountNumber,
          type: 'place',
          image: props.property.images?.length
            ? {
                url: props.property.images[0].url,
                width: props.property.images[0].width,
                height: props.property.images[0].height,
              }
            : undefined,
          updatedTime: props.property.listingUpdatedTime,
          latLng: props.property.coordinate,
        })}
      </Head>

      {!props.property.featuredDevelopmentContent && (
        <AdBox
          id={`PDPtop/PDPtop_${adSuffix}`}
          config="PDPtop"
          sizes={AD_BOX_1_SIZES}
          hideOver={780}
          obscured={isAdObscuringModalOpen}
          containerStyle={AD_BOX_1_STYLE}
          refresh
          timedRefresh
        />
      )}

      <PropertyDetailsContent
        {...props}
        {...callAgent}
        adSuffix={adSuffix}
        images={images}
        updateSectionUrl={updateSectionUrl}
        imageIndex={imageIndex}
        updatePhotoUrl={updatePhotoUrl}
        handleImageClick={handleImageClick}
        showSlideshowModal={showSlideshowModal}
        getReplaceUrl={getReplaceUrl}
        hasSitemap={hasSitemap}
        hasFeaturedBrochure={hasFeaturedBrochure}
        urlParts={urlParts}
        favourited={favourited}
        toggleFavourited={toggleFavourited}
        leadId={leadId}
        handleInfoClick={handleInfoClick}
        buyingStatus={buyingStatus}
        ukResident={ukResident}
        mortgageCalculator={mortgageCalculator}
        onFavourite={onFavourite}
        handleShare={handleShare}
        isFeaturedDevelopment={isFeaturedDevelopment}
        similarProperties={similarProperties}
        fetchSimilarProperties={fetchSimilarProperties}
        showMapModal={showMapModal}
        isMobile={isMobile}
        openSections={openSections}
        renderAccordionHeader={renderAccordionHeader}
        showLegalFees={showLegalFees}
        hasLegalContact={hasLegalContact}
        setEnquiryAccountNumber={setEnquiryAccountNumber}
        showSchoolData={showSchoolData}
        showPromoteText={showPromoteText}
        marketTrendsData={marketTrendsData}
        marketTrendsError={marketTrendsError}
        marketTrendsLoading={marketTrendsLoading}
        fetchMarketTrendsData={fetchMarketTrendsData}
        otherDevelopments={otherDevelopments}
        fetchOtherDevelopments={fetchOtherDevelopments}
        modalImage={modalImage}
        setModalImage={setModalImage}
        showStampDutyModal={showStampDutyModal}
        setBuyingStatus={setBuyingStatus}
        setUkResident={setUkResident}
        handleBack={handleBack}
        showEnquiryModal={showEnquiryModal}
        showRegisterInterestModal={showRegisterInterestModal}
        showRequestValuationModal={showRequestValuationModal}
        enquiryAccountNumber={enquiryAccountNumber}
        setLeadId={setLeadId}
        showLegalFeesModal={showLegalFeesModal}
        showBrochureModal={showBrochureModal}
        showReportModal={showReportModal}
        showShareModal={showShareModal}
        isAdObscuringModalOpen={isAdObscuringModalOpen}
      />
    </>
  );
};

export default React.memo(PropertyDetails);
